import React, { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/Layout';
import ScrollIndicator from './components/ScrollIndicator';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';

// Create a lazy component wrapper with error boundary
const LazyComponent = (importFunc) => {
  const Component = lazy(importFunc);
  return (props) => (
    <ErrorBoundary>
      <Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}>
        <Component {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

// Lazy load all pages with improved error handling
const About = LazyComponent(() => import('./pages/About'));
const Admin = LazyComponent(() => import('./pages/Admin'));
const ArticleDetail = LazyComponent(() => import('./pages/ArticleDetail'));
const Articles = LazyComponent(() => import('./pages/Articles'));
const Campaign = LazyComponent(() => import('./pages/Campaign'));
const CampaignDetail = LazyComponent(() => import('./pages/CampaignDetail'));
const Contact = LazyComponent(() => import('./pages/Contact'));
const Donate = LazyComponent(() => import('./pages/Donate'));
const Home = LazyComponent(() => import('./pages/Home'));
const Bangladesh2 = LazyComponent(() => import('./pages/Bangladesh2'));
const SecondRepublic = LazyComponent(() => import('./pages/SecondRepublic'));
const Join = LazyComponent(() => import('./pages/Join'));
const Leadership = LazyComponent(() => import('./pages/Leadership'));
const Media = LazyComponent(() => import('./pages/Media'));
const Programs = LazyComponent(() => import('./pages/Programs'));
const UpdatesLatest = LazyComponent(() => import('./pages/UpdatesLatest'));
const UpdatesFeatured = LazyComponent(() => import('./pages/UpdatesFeatured'));
const UpdatesNoticeBoard = LazyComponent(() => import('./pages/UpdatesNoticeBoard'));
const VolunteerOpportunities = LazyComponent(() => import('./pages/VolunteerOpportunities'));
const VolunteerFAQs = LazyComponent(() => import('./pages/VolunteerFAQs'));
const CandidatesMap = LazyComponent(() => import('./pages/CandidatesMap'));
const ABWings = LazyComponent(() => import('./pages/ABWings'));
const Shop = LazyComponent(() => import('./pages/Shop'));
const ProductDetail = LazyComponent(() => import('./pages/ProductDetail'));
const Checkout = LazyComponent(() => import('./pages/Checkout'));

const queryClient = new QueryClient();

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <Router basename={process.env.PUBLIC_URL}>
              <ScrollIndicator />
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Layout><Home /></Layout>} />
                <Route path="/about" element={<Layout><About /></Layout>} />
                <Route path="/programs" element={<Layout><Programs /></Layout>} />
                <Route path="/leadership" element={<Layout><Leadership /></Layout>} />
                <Route path="/media" element={<Layout><Media /></Layout>} />
                <Route path="/articles" element={<Layout><Articles /></Layout>} />
                <Route path="/articles/:param" element={<Layout><ArticleDetail /></Layout>} />
                {/* New landing pages that mirror roadmap sections */}
                <Route path="/bangladesh-2-0" element={<Layout><Bangladesh2 /></Layout>} />
                <Route path="/second-republic" element={<Layout><SecondRepublic /></Layout>} />
                <Route path="/campaigns" element={<Layout><Campaign /></Layout>} />
                <Route path="/campaigns/:param" element={<Layout><CampaignDetail /></Layout>} />
                {/* Simple status filters via param values current|past|upcoming */}
                <Route path="/campaigns/current" element={<Layout><Campaign /></Layout>} />
                <Route path="/campaigns/past" element={<Layout><Campaign /></Layout>} />
                <Route path="/campaigns/upcoming" element={<Layout><Campaign /></Layout>} />
                <Route path="/contact" element={<Layout><Contact /></Layout>} />
                <Route path="/join" element={<Layout><Join /></Layout>} />
                <Route path="/donate" element={<Layout><Donate /></Layout>} />
                {/* Updates */}
                <Route path="/updates/latest" element={<Layout><UpdatesLatest /></Layout>} />
                <Route path="/updates/featured" element={<Layout><UpdatesFeatured /></Layout>} />
                <Route path="/updates/notice-board" element={<Layout><UpdatesNoticeBoard type="notice" /></Layout>} />
                <Route path="/press-releases" element={<Layout><UpdatesNoticeBoard type="press" /></Layout>} />
                <Route path="/volunteer-opportunities" element={<Layout><VolunteerOpportunities /></Layout>} />
                <Route path="/volunteer-faqs" element={<Layout><VolunteerFAQs /></Layout>} />
                <Route path="/candidates-map" element={<Layout><CandidatesMap /></Layout>} />
                <Route path="/ab-wings" element={<Layout><ABWings /></Layout>} />
                {/* Shop */}
                <Route path="/shop" element={<Layout><Shop /></Layout>} />
                <Route path="/shop/:param" element={<Layout><ProductDetail /></Layout>} />
                <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
                {/* Optionally keep the old /campaign route for backward compatibility */}
                <Route path="/campaign" element={<Layout><Campaign /></Layout>} />

                {/* Protected Admin Route */}
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </Router>
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
